import { useEffect, useState } from 'react';
import { Input } from '@components/common/Input';
import { contentApi } from '@api/contentApi';
import {
    DndContext,
    useDraggable,
    useDroppable,
    rectIntersection,
    DragOverlay,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import PropTypes from 'prop-types';
import { employeeApi } from '@api/employeeApi';
import { useParams } from 'react-router-dom';
import { cn } from '@helpers/styleUtils';
import { CommonUtils } from '@helpers/commonUtils';
import { ScrollArea } from '@components/common/ScrollArea';
import { getWidgetPreviewImg } from '@utils/imageUtils';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { GlobalLoading } from '@components/loaders/GlobalLoading';

export const EditWidgets = () => {
    const { role_id } = useParams();
    const [widgets, setWidgets] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [widgetConfig, setWidgetConfig] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [roleData, setRoleData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchWidgets = async () => {
            const data = await contentApi.getAllWidgets();
            setWidgets(data || []);
        };
        fetchWidgets();
    }, []);

    useEffect(() => {
        const fetchWidgetConfig = async () => {
            try {
                const response = await employeeApi.getRoleList();
                const selectedRole = response.find(
                    role => role.role_name.toLowerCase() === role_id.toLowerCase(),
                );
                setRoleData(selectedRole);
                setWidgetConfig(
                    selectedRole?.widget_config
                        ? CommonUtils.jsonSafeParse(selectedRole.widget_config)
                        : [],
                );

                setLoading(false);
            } catch (err) {
                console.log(err);
            }
        };

        fetchWidgetConfig();
    }, [role_id]);

    useEffect(() => {
        return () => {
            setActiveId(null); // Cleanup on unmount
        };
    }, []);

    const handleDrop = event => {
        console.log('Handle Drop:', event);
        const widgetId = event.active.id;
        const droppedWidget = widgets.find(widget => widget.id === widgetId);

        // Check if the widget is being dropped in the drop area
        if (event.over) {
            const overWidgetName = event.over.id;
            const overIndex = widgetConfig.findIndex(w => w.widgetName === overWidgetName);

            // If the widget is already in the config, reorder it
            if (widgetConfig.some(w => w.widgetName === droppedWidget.widgetName)) {
                const oldIndex = widgetConfig.findIndex(
                    w => w.widgetName === droppedWidget.widgetName,
                );
                if (oldIndex !== overIndex && oldIndex !== -1 && overIndex !== -1) {
                    handleReorder(oldIndex, overIndex);
                }
            } else {
                // Directly add the widget to config if it's new
                const newWidget = {
                    widgetName: droppedWidget.widgetName,
                    description: droppedWidget.description,
                    widgetPreviewImage: droppedWidget.widgetPreviewImage,
                    widgetSize: droppedWidget.size || 'small',
                    widgetPosition: widgetConfig.length + 1,
                };

                setWidgetConfig(prev => [...prev, newWidget]);
            }
        }
    };

    const { setNodeRef: setDropNodeRef, isOver } = useDroppable({
        id: 'drop-area',
        onDrop: handleDrop,
    });

    const handleDragEnd = event => {
        console.log('Drag end event:', event);
        const { active, over } = event;
        setActiveId(null);

        if (!over) {
            console.log('No valid drop target');
            return;
        }

        // Check if dragging from widget list
        if (active.data.current?.isNew) {
            const newWidget = {
                widgetName: active.data.current.widget.widgetName,
                description: active.data.current.widget.description,
                widgetPreviewImage: active.data.current.widget.widgetPreviewImage,
                widgetSize: active.data.current.widget.size || 'small',
                widgetPosition: widgetConfig.length + 1,
            };

            setWidgetConfig(prev => [...prev, newWidget]);
            console.log('Added new widget:', newWidget);
        }
        // Handle reordering
        else if (active.data.current) {
            const oldIndex = widgetConfig.findIndex(w => w.widgetName === active.id);
            const newIndex = widgetConfig.findIndex(w => w.widgetName === over.id);

            if (oldIndex !== newIndex && oldIndex !== -1 && newIndex !== -1) {
                handleReorder(oldIndex, newIndex);
                console.log('Reordered widgets:', oldIndex, 'to', newIndex);
            }
        }
    };

    const handleDragStart = event => {
        setActiveId(event.active.data.current.widget);
    };

    const handleReorder = (oldIndex, newIndex) => {
        setWidgetConfig(prev => {
            const updated = [...prev];
            const [movedItem] = updated.splice(oldIndex, 1);
            updated.splice(newIndex, 0, movedItem);

            // Update positions
            return updated.map((widget, index) => ({
                ...widget,
                widgetPosition: index + 1,
            }));
        });
    };

    const saveWidgetConfig = async () => {
        try {
            setLoading(true);

            const payload = {
                id: roleData.role_id,
                widgetConfig: JSON.stringify(widgetConfig),
            };

            await contentApi.updateRoleWidgetConfig(payload);
            setLoading(false);
            // Show success notification
        } catch (error) {
            console.error('Error saving widget config:', error);
            setLoading(false);
            // Show error notification
        }
    };

    // Debugging: Log when the drop area is over
    useEffect(() => {
        console.log('Is over drop area:', isOver);
    }, [isOver]);

    const dropAreaClass = isOver
        ? 'border-2 border-blue-500 bg-blue-50'
        : 'border-2 border-dashed border-gray-300';
    const DraggableWidget = ({ widget, isList = true }) => {
        const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
            id: widget.id,
            data: {
                isNew: true, // Indicates it's from widget list
                widget: {
                    ...widget,
                    widgetPosition: null, // Position will be assigned on drop
                },
            },
        });

        return (
            <div
                ref={setNodeRef}
                {...listeners}
                {...attributes}
                className={`flex flex-col gap-2 items-center cursor-move ${
                    isDragging ? 'opacity-50' : ''
                }`}
            >
                <div
                    className={cn(
                        'bg-slate-300 rounded-md p-2 w-full flex items-center justify-center',
                        isList ? 'h-[128px]' : '',
                    )}
                >
                    <img
                        src={getWidgetPreviewImg(widget.widgetName)}
                        className="h-full object-contain w-full"
                        alt={widget.widgetName.replaceAll('_', ' ')}
                    />
                </div>
                {isList && (
                    <p className="text-md font-bold">{widget.widgetName.replaceAll('_', ' ')}</p>
                )}
            </div>
        );
    };

    const DraggableConfigWidget = ({ widget }) => {
        const { attributes, listeners, setNodeRef, isDragging } = useSortable({
            id: widget.widgetName, // Use widgetName as id for config items
        });

        return (
            <div
                ref={setNodeRef}
                {...listeners}
                {...attributes}
                className={cn(
                    'cursor-move',
                    widget.widgetSize === 'large' ? 'col-span-2' : 'col-span-1',
                    isDragging ? 'opacity-50' : '',
                )}
            >
                <img
                    src={getWidgetPreviewImg(widget.widgetName)}
                    className="w-full object-cover"
                    alt={widget.widgetName.replaceAll('_', ' ')}
                />
            </div>
        );
    };

    DraggableConfigWidget.propTypes = {
        isList: PropTypes.bool,
        widget: PropTypes.shape({
            id: PropTypes.string,
            widgetPreviewImage: PropTypes.string.isRequired,
            widgetName: PropTypes.string.isRequired,
            widgetSize: PropTypes.oneOf(['small', 'large']),
            widgetPosition: PropTypes.string,
        }),
    };

    DraggableWidget.propTypes = {
        isList: PropTypes.bool,
        widget: PropTypes.shape({
            id: PropTypes.string.isRequired,
            widgetPreviewImage: PropTypes.string.isRequired,
            widgetName: PropTypes.string.isRequired,
        }),
    };

    const filteredWidgetConfig =
        widgetConfig && widgetConfig.length > 0
            ? widgetConfig.sort((a, b) => a.widgetPosition - b.widgetPosition)
            : [];

    const filteredWidgetList = widgets.filter(widget =>
        widgetConfig && widgetConfig.length > 0
            ? !widgetConfig.some(
                  configWidget =>
                      configWidget.widgetName.toUpperCase() === widget.widgetName.toUpperCase(),
              )
            : widget,
    );

    return (
        <DndContext
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            collisionDetection={rectIntersection}
            modifiers={[restrictToWindowEdges]}
        >
            {loading ? (
                <GlobalLoading />
            ) : (
                <SortableContext items={filteredWidgetConfig.map(widget => widget.widgetName)}>
                    <div className="flex items-start gap-4 relative">
                        <div className={`w-8/12 ${dropAreaClass} border-2 border-dashed`}>
                            <div
                                className="grid grid-cols-2 gap-2"
                                ref={setDropNodeRef}
                                id="drop-area"
                            >
                                {filteredWidgetConfig && filteredWidgetConfig.length > 0 ? (
                                    filteredWidgetConfig.map(widget => (
                                        <DraggableConfigWidget
                                            key={widget.widgetName}
                                            widget={widget}
                                        />
                                    ))
                                ) : (
                                    <div className="col-span-2 h-screen flex flex-col items-center justify-center">
                                        <p className="text-sm text-gray-500">No Widget Config!</p>
                                        <p className="text-sm text-gray-500">
                                            Please Drag widgets from list.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="w-4/12 bg-white rounded-[10px] shadow-md p-4 sticky top-20">
                            {/* All Widgets List */}
                            <div>
                                <p className="text-xl font-bold mb-4">Widgets List</p>
                                <Input
                                    className="h-12 w-full text-lg font-bold mb-4"
                                    placeholder="Search Widget"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                                <ScrollArea className="h-[calc(100vh_-_350px)]">
                                    <div className="grid grid-cols-2 gap-4">
                                        {filteredWidgetList
                                            .filter(widget =>
                                                widget.widgetName
                                                    .replaceAll('_', ' ')
                                                    .toLowerCase()
                                                    .includes(searchQuery.toLowerCase()),
                                            )
                                            .map((widget, index) => (
                                                <DraggableWidget key={index} widget={widget} />
                                            ))}
                                    </div>
                                </ScrollArea>
                                <div className="flex justify-end mt-4">
                                    <button
                                        className="bg-blue-500 text-white px-4 py-2 rounded"
                                        onClick={saveWidgetConfig}
                                    >
                                        Save Layout
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </SortableContext>
            )}
            <DragOverlay>
                {activeId ? (
                    <div className="bg-white shadow-lg rounded-md p-2 opacity-80">
                        <img
                            src={getWidgetPreviewImg(activeId.widgetName)}
                            className="h-[128px] object-contain w-full pointer-events-none"
                            alt={activeId.widgetName.replaceAll('_', ' ')}
                        />
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};
